import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import { budgetDialogPaperProps } from "components/Budget/budgetDialog";
import {
  IBudgetChapter,
  IBudgetDivision,
  IBudgetParagraph,
} from "dto/Budget/IBudgetDictionaries";
import ModalsManagerContext from "contexts/ModalsManager";
import { formatMoney } from "utils/formatMoney";
import AmountField from "components/CreateRegistryEntryModal/AmountField";
import { ENotificationType, notification } from "utils/notification";
import { randomString } from "utils/randomString";
import styles from "./styles.module.scss";

interface QuotaRow {
  key: string;
  paragraph: IBudgetParagraph | null;
  paragraphId: string;
  suffix: string;
  amount: string;
}

const emptyRow = (): QuotaRow => ({
  key: randomString(),
  paragraph: null,
  paragraphId: "",
  suffix: "0",
  amount: "",
});

const suffixes = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const CreateBudgetExpenseModal = ({ year = DEFAULT_BUDGET_YEAR }: { year?: number }) => {
  const navigate = useNavigate();
  const { modals, setModals } = useContext(ModalsManagerContext);

  const [division, setDivision] = useState<IBudgetDivision | null>(null);
  const [divisionInput, setDivisionInput] = useState("");
  const [divisionSearch, setDivisionSearch] = useState("");
  const divisionId = division?.Id ?? "";
  const [chapter, setChapter] = useState<IBudgetChapter | null>(null);
  const chapterId = chapter?.Id ?? "";
  const [chapterInput, setChapterInput] = useState("");
  const [chapterSearch, setChapterSearch] = useState("");
  const [paragraphInput, setParagraphInput] = useState("");
  const [paragraphSearch, setParagraphSearch] = useState("");
  const [title, setTitle] = useState("");
  const [openingBalance, setOpeningBalance] = useState(false);
  const [openingBalanceAmount, setOpeningBalanceAmount] = useState("");
  const [sharedTask, setSharedTask] = useState(false);
  const [rows, setRows] = useState<QuotaRow[]>([emptyRow()]);

  const { data: divisions = [], isFetching: divisionsLoading } =
    budgetService.useGetDivisions(divisionSearch);
  const { data: chapters = [], isFetching: chaptersLoading } =
    budgetService.useGetChapters(divisionId, chapterSearch);
  const { data: paragraphs = [], isFetching: paragraphsLoading } =
    budgetService.useGetParagraphs(paragraphSearch);
  const { mutate: createExpense, isLoading } = budgetService.useCreateExpense();

  // Debounce zapytań wyszukiwania słowników do BE.
  useEffect(() => {
    const t = setTimeout(() => setDivisionSearch(divisionInput), 300);
    return () => clearTimeout(t);
  }, [divisionInput]);
  useEffect(() => {
    const t = setTimeout(() => setChapterSearch(chapterInput), 300);
    return () => clearTimeout(t);
  }, [chapterInput]);
  useEffect(() => {
    const t = setTimeout(() => setParagraphSearch(paragraphInput), 300);
    return () => clearTimeout(t);
  }, [paragraphInput]);

  // Lista opcji paragrafu dla wiersza — zawiera wybrany paragraf, nawet gdy
  // wynik wyszukiwania go nie zawiera (żeby wartość pozostała widoczna).
  const paragraphOptionsFor = (row: QuotaRow) =>
    row.paragraph && !paragraphs.some((p) => p.Id === row.paragraph?.Id)
      ? [row.paragraph, ...paragraphs]
      : paragraphs;

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
    [rows],
  );

  const validRows = rows.filter(
    (r) => r.paragraphId && (parseFloat(r.amount) || 0) > 0,
  );
  const canSubmit =
    !!divisionId && !!chapterId && !!title.trim() && validRows.length > 0;

  const handleClose = () =>
    setModals({ ...modals, createBudgetExpense: false, hide: false });

  const updateRow = (key: string, patch: Partial<QuotaRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (key: string) =>
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      return next.length ? next : [emptyRow()];
    });

  const handleSubmit = () => {
    if (!canSubmit) return;
    createExpense(
      {
        Year: year,
        DivisionId: divisionId,
        ChapterId: chapterId,
        Title: title.trim(),
        OpeningBalance: openingBalance,
        OpeningBalanceAmount: openingBalance
          ? parseFloat(openingBalanceAmount) || 0
          : 0,
        SharedTask: sharedTask,
        Quotas: validRows.map((r) => ({
          ParagraphId: r.paragraphId,
          ParagraphSuffix: r.suffix,
          Amount: parseFloat(r.amount),
        })),
      },
      {
        onSuccess: () => {
          handleClose();
          navigate("/budget");
        },
        onError: () =>
          notification({
            title: "Wystąpił błąd",
            text: "Nie udało się utworzyć wydatku.",
            type: ENotificationType.ERROR,
          }),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={budgetDialogPaperProps}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>Karta rejestracji wydatku</span>
          <IconButton onClick={handleClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        <div className={styles.content}>
          <Autocomplete
            fullWidth
            size="small"
            options={divisions}
            value={division}
            onChange={(_, val) => {
              setDivision(val);
              setChapter(null);
              setChapterInput("");
              setChapterSearch("");
            }}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setDivisionInput(value);
              else if (reason === "clear") setDivisionInput("");
            }}
            getOptionLabel={(d) => `${d.Symbol} – ${d.Name}`}
            isOptionEqualToValue={(o, v) => o.Id === v.Id}
            filterOptions={(options) => options}
            openOnFocus
            loading={divisionsLoading}
            loadingText="Wyszukiwanie…"
            noOptionsText="Brak działów"
            renderInput={(params) => (
              <TextField {...params} label="Dział" size="small" />
            )}
          />

          <Autocomplete
            fullWidth
            size="small"
            disabled={!divisionId}
            options={chapters}
            value={chapter}
            onChange={(_, val) => setChapter(val)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") setChapterInput(value);
              else if (reason === "clear") setChapterInput("");
            }}
            getOptionLabel={(c) => `${c.Symbol} – ${c.Name}`}
            isOptionEqualToValue={(o, v) => o.Id === v.Id}
            filterOptions={(options) => options}
            openOnFocus
            loading={chaptersLoading}
            loadingText="Wyszukiwanie…"
            noOptionsText="Brak rozdziałów"
            renderInput={(params) => (
              <TextField {...params} label="Rozdział" size="small" />
            )}
          />

          <TextField
            label="Tytuł"
            size="small"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className={styles.checkboxes}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.checked)}
                />
              }
              label="Bilans otwarcia"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sharedTask}
                  onChange={(e) => setSharedTask(e.target.checked)}
                />
              }
              label="Zadanie wspólne"
            />
          </div>

          {openingBalance && (
            <AmountField
              liveFormat
              label="Kwota bilansu otwarcia"
              value={openingBalanceAmount}
              onChange={setOpeningBalanceAmount}
            />
          )}

          <div className={styles.paragraphsHeader}>
            <span className={styles.sectionTitle}>Paragrafy</span>
            <IconButton onClick={addRow} size="small" color="primary">
              +
            </IconButton>
          </div>

          <div className={styles.gridHead}>
            <span>Paragraf</span>
            <span>4.ta cyfra §</span>
            <span>Kwota</span>
            <span />
          </div>

          {rows.map((row) => (
            <div className={styles.row} key={row.key}>
              <Autocomplete
                size="small"
                fullWidth
                options={paragraphOptionsFor(row)}
                value={row.paragraph}
                onChange={(_, val) =>
                  updateRow(row.key, {
                    paragraph: val,
                    paragraphId: val?.Id ?? "",
                  })
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input") setParagraphInput(value);
                  else if (reason === "clear") setParagraphInput("");
                }}
                getOptionLabel={(p) => `${p.Symbol} – ${p.Name}`}
                isOptionEqualToValue={(o, v) => o.Id === v.Id}
                filterOptions={(options) => options}
                openOnFocus
                loading={paragraphsLoading}
                loadingText="Wyszukiwanie…"
                noOptionsText="Brak paragrafów"
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Wybierz paragraf" />
                )}
              />

              <FormControl size="small">
                <Select
                  value={row.suffix}
                  onChange={(e) => updateRow(row.key, { suffix: e.target.value })}
                >
                  {suffixes.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <AmountField
                liveFormat
                value={row.amount}
                onChange={(v) => updateRow(row.key, { amount: v })}
              />

              <IconButton onClick={() => removeRow(row.key)} size="small">
                ×
              </IconButton>
            </div>
          ))}

          <div className={styles.total}>
            <span>Budżet zadania</span>
            <strong>{formatMoney(total)}</strong>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.submit}
            disabled={!canSubmit || isLoading}
            onClick={handleSubmit}
          >
            Utwórz
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateBudgetExpenseModal;
